import { FunctionComponent, useEffect, useState } from 'react'
import { Daycare } from '@/types/Daycare'
import profileStyles from '../DaycareProfileHandler.module.scss'
import StaffSuggest from './staffSuggest/staffSuggest'
import ParentSuggest from './parentSuggest/parentSuggest'
import { useQuery } from '@apollo/client'
import Loading from '@/components/Loading/Loading'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import { useTranslation } from 'react-i18next'
import { GET_SUGGEST_SETTINGS } from '@/modules/API/daycare/daycareSuggestHandler'
import { SuggestSettingsResponse } from '@/types/Suggest'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import Assignment from '@mui/icons-material/Assignment'
import { useAuth } from '@/modules/context/AuthContext.tsx'
import NotificationSuggest from '@/container/daycare/daycareProfile/suggestSettings/notificationSuggest/notificationSuggest.tsx'

interface SuggestProps {
    daycare: Daycare
}

const SuggestSettings: FunctionComponent<SuggestProps> = ({ daycare }: SuggestProps) => {
    const { user } = useAuth()

    const { t } = useTranslation()

    const navigate: NavigateFunction = useNavigate()

    const [suggestStaffDistance, setSuggestStaffDistance] = useState<string>('distance_5')
    const [suggestParentDistance, setSuggestParentDistance] = useState<string>('distance_0')
    const [suggestParentMinMonth, setSuggestParentMinMonth] = useState<number>(0)
    const [suggestParentMaxMonth, setSuggestParentMaxMonth] = useState<number>(0)
    const [suggestContactEmails, setSuggestContactEmails] = useState<string[]>([])
    const [suggestNotification, setSuggestNotification] = useState<boolean>(true)

    const {
        loading: loadingSuggest,
        data: dataSuggest,
        error: errorSuggest
    } = useQuery<{ getSuggestSettings: SuggestSettingsResponse }>(GET_SUGGEST_SETTINGS, {
        fetchPolicy: 'network-only',
        variables: {
            daycareId: daycare.id
        }
    })

    useEffect(() => {
        if (dataSuggest) {
            setSuggestStaffDistance(dataSuggest.getSuggestSettings.staffDistance)
            setSuggestParentDistance(dataSuggest.getSuggestSettings.parentDistance)
            setSuggestParentMinMonth(dataSuggest.getSuggestSettings.parentMinMonth)
            setSuggestParentMaxMonth(dataSuggest.getSuggestSettings.parentMaxMonth)
            setSuggestContactEmails(dataSuggest.getSuggestSettings.contactEmails)
            setSuggestNotification(dataSuggest.getSuggestSettings.notification)
        }
    }, [dataSuggest])

    const canUpdate: boolean = !!user?.currentDaycareRights.canWriteParamsDaycare

    return (
        <div className={profileStyles.PagePosition}>
            {loadingSuggest && <Loading overall={false} />}
            {dataSuggest && (
                <>
                    <ParentSuggest
                        daycare={daycare}
                        parentDistance={suggestParentDistance}
                        parentMinMonth={suggestParentMinMonth}
                        parentMaxMonth={suggestParentMaxMonth}
                        setParentDistance={setSuggestParentDistance}
                        setParentMaxMonth={setSuggestParentMaxMonth}
                        setParentMinMonth={setSuggestParentMinMonth}
                        canUpdate={canUpdate}
                    />
                    <StaffSuggest
                        daycare={daycare}
                        staffDistance={suggestStaffDistance}
                        setStaffDistance={setSuggestStaffDistance}
                        canUpdate={canUpdate}
                    />
                    <NotificationSuggest
                        daycare={daycare}
                        suggestContactEmails={suggestContactEmails}
                        setSuggestContactEmails={setSuggestContactEmails}
                        suggestNotification={suggestNotification}
                        setSuggestNotification={setSuggestNotification}
                        canUpdate={canUpdate}
                    />
                </>
            )}
            {errorSuggest && <ErrorMessage error={t('generalError')} />}
            {user?.currentDaycareRights.hasSuggestAccess && user.currentDaycareRights.canReadSuggestions && (
                <ButtonTextIcon
                    type={'custom'}
                    customText={t('showSuggestions')}
                    customIcon={<Assignment />}
                    onClick={() => navigate('/suggest')}
                />
            )}
        </div>
    )
}

export default SuggestSettings
